import { useState } from 'react'
import { Avatar, Icon, Pill } from '@opallabs/lapidary'
import * as styles from './styles'
import { VersionLabelEditor } from './VersionLabelEditor'

const formatDate = (isoDate: string): string =>
  new Date(isoDate).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

type VersionBylineProps = {
  authorName: string
  createdAt: string
  label: string | null
  /** Labelling a version needs edit permission on the content; viewers only read them. */
  canEdit: boolean
  onLabelCommit: (label: string | null) => void
}

/**
 * Attribution bar under the preview canvas: who edited the version and when, plus
 * the version's label. This is the only place labels can be set, so an "Add label"
 * chip is shown when unset — to anyone who may set one.
 */
export const VersionByline: React.VFC<VersionBylineProps> = ({
  authorName,
  createdAt,
  label,
  canEdit,
  onLabelCommit,
}) => {
  const [isEditing, setIsEditing] = useState(false)

  const commit = (next: string | null) => {
    setIsEditing(false)
    onLabelCommit(next)
  }

  return (
    <div css={styles.byline}>
      <div css={styles.bylineAuthor}>
        <Avatar fullName={authorName} size={24} />
        <div css={styles.bylineText}>
          <span css={styles.bylineName}>Edited by {authorName}</span>
          <span css={styles.bylineDate}>{formatDate(createdAt)}</span>
        </div>
      </div>
      <div css={styles.bylineLabel}>
        {!canEdit ? (
          label && <Pill text={label} color="purple" size="small" solid />
        ) : isEditing ? (
          <VersionLabelEditor
            initialValue={label ?? ''}
            onCommit={commit}
            onCancel={() => setIsEditing(false)}
          />
        ) : label ? (
          <button
            type="button"
            css={styles.addLabelButton}
            aria-label="Edit label"
            onClick={() => setIsEditing(true)}
          >
            <Pill text={label} color="purple" size="small" solid />
          </button>
        ) : (
          <button
            type="button"
            css={styles.addLabelChip}
            aria-label="Add label"
            onClick={() => setIsEditing(true)}
          >
            <Icon glyph="gem-label" size={12} ariaHidden />
            Add label
          </button>
        )}
      </div>
    </div>
  )
}
